import { useEffect, useRef, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useSetCurrentMedia, useSetPredictionsForFileAndFrameSegment } from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useProjectSelector } from "@rapid/contexts/ProjectSelectorContext";
import useApi from "@rapid/hooks/useApi";
import { MediaItemFile } from "@rapid/types/media";
import {
    SourceDocument,
    getCachedInferenceResults,
    setCachedInferenceResults
} from "@rapid/utils/api/datasets";
import { useTestWithMoreFiles } from "@rapid/useTestWithMoreFiles";

/**
 * Download an image from a URL and convert it to a File object.
 * Uses the sourceId as the file.id to maintain consistency with the source document.
 * Sets skipUpload=true since these files are already hosted.
 * Stores the imageUrl so backend can use it directly instead of looking up in storage.
 */
async function downloadImageAsFile(
    url: string,
    filename: string,
    sourceId: string
): Promise<MediaItemFile> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" }) as MediaItemFile;
    // Use sourceId as the file ID to maintain consistency with source documents
    file.id = sourceId;
    // Mark as skipUpload since this file is already hosted
    file.skipUpload = true;
    // Store the image URL so backend can use it directly for inference
    file.imageUrl = url;
    return file;
}

/**
 * Build the CDN URL for a source image
 */
function getSourceImageUrl(source: SourceDocument, cdnHost: string): string {
    return `https://${cdnHost}/${source.owner}/${source.id}/original.jpg`;
}

// Track loading state per workflow (not "already loaded" - just current loading)
// This prevents duplicate simultaneous loads for the same workflow
const loadingWorkflowsSet = new Set<string>();

/**
 * Hook to load a random source from the dataset when the deploy page has no media.
 * This provides a sample image for users who land on the deploy page directly.
 */
export function useRandomSourceLoader() {
    const { workflowUrl } = useParams<{ workflowUrl: string }>();
    const { currentWorkflow } = useCurrentWorkflow();
    const { mediaItems, addMediaItems } = useMedia();
    const { testWithMoreFiles } = useTestWithMoreFiles();
    const projectSelector = useProjectSelector();
    const api = useApi();
    const setCurrentMedia = useSetCurrentMedia();
    const setPredictionsForFileAndFrameSegment = useSetPredictionsForFileAndFrameSegment();

    const [isLoadingRandomSource, setIsLoadingRandomSource] = useState(false);

    // Track if we've loaded for the current workflowUrl in this component instance
    // This resets when the component remounts (e.g., when switching workflows)
    const hasLoadedForCurrentWorkflowRef = useRef<string | null>(null);

    const loadRandomSource = useCallback(
        async (targetRapidDataId: string, targetWorkflowUrl: string) => {
            console.log("[useRandomSourceLoader] ========== loadRandomSource CALLED ==========");
            console.log("[useRandomSourceLoader] targetWorkflowUrl:", targetWorkflowUrl);
            console.log("[useRandomSourceLoader] targetRapidDataId:", targetRapidDataId);

            if (!targetRapidDataId) {
                console.log("[useRandomSourceLoader] No rapidDataId available - RETURNING EARLY");
                return;
            }

            if (!targetWorkflowUrl) {
                console.log("[useRandomSourceLoader] No workflowUrl available - RETURNING EARLY");
                return;
            }

            if (loadingWorkflowsSet.has(targetWorkflowUrl)) {
                console.log(
                    "[useRandomSourceLoader] Already loading this workflow - RETURNING EARLY"
                );
                return;
            }

            loadingWorkflowsSet.add(targetWorkflowUrl);
            setIsLoadingRandomSource(true);

            try {
                console.log(
                    "[useRandomSourceLoader] Fetching random source for rapidDataId:",
                    targetRapidDataId,
                    "workflowUrl:",
                    targetWorkflowUrl
                );
                const source = await api.datasets.getRandomSource(targetRapidDataId);

                if (!source) {
                    console.log("[useRandomSourceLoader] No source found in dataset");
                    return;
                }

                console.log("[useRandomSourceLoader] Got random source:", source.id);

                // Build the image URL
                const cdnHost = projectSelector.cdnHost();
                const imageUrl = getSourceImageUrl(source, cdnHost);
                const filename = source.name || `${source.id}.jpg`;

                console.log("[useRandomSourceLoader] Downloading image:", imageUrl);

                // Download and convert to file
                const file = await downloadImageAsFile(imageUrl, filename, source.id);

                // Check if we have cached predictions for this workflow
                // This happens when returning from /improve to /deploy
                const cachedResults = getCachedInferenceResults(targetWorkflowUrl);

                if (cachedResults) {
                    console.log(
                        "[useRandomSourceLoader] CACHE HIT - Restoring cached predictions for:",
                        targetWorkflowUrl
                    );

                    // Add media without running inference
                    const createdMediaItems = await addMediaItems([file]);

                    // Restore predictions from cache
                    setPredictionsForFileAndFrameSegment(cachedResults.predictions);

                    // Navigate to the loaded file
                    if (createdMediaItems && createdMediaItems.length > 0) {
                        const loadedMediaItem = createdMediaItems[0];
                        console.log(
                            "[useRandomSourceLoader] Setting current media to:",
                            loadedMediaItem.id
                        );
                        setCurrentMedia({ file: loadedMediaItem });
                    }

                    console.log("[useRandomSourceLoader] Restored cached predictions successfully");
                } else {
                    console.log(
                        "[useRandomSourceLoader] CACHE MISS - Running inference for:",
                        targetWorkflowUrl
                    );

                    // Add file and run inference
                    const createdMediaItems = await testWithMoreFiles([file]);

                    // Navigate to the loaded file by setting it as current media
                    if (createdMediaItems && createdMediaItems.length > 0) {
                        const loadedMediaItem = createdMediaItems[0];
                        console.log(
                            "[useRandomSourceLoader] Setting current media to:",
                            loadedMediaItem.id
                        );
                        setCurrentMedia({ file: loadedMediaItem });
                    }
                }

                hasLoadedForCurrentWorkflowRef.current = targetWorkflowUrl;
                console.log(
                    "[useRandomSourceLoader] Successfully loaded random source for:",
                    targetWorkflowUrl
                );
            } catch (err) {
                console.error("[useRandomSourceLoader] Error loading random source:", err);
            } finally {
                loadingWorkflowsSet.delete(targetWorkflowUrl);
                setIsLoadingRandomSource(false);
            }
        },
        [
            api.datasets,
            projectSelector,
            testWithMoreFiles,
            addMediaItems,
            setCurrentMedia,
            setPredictionsForFileAndFrameSegment
        ]
    );

    // Load a random source when:
    // 1. We have a rapidDataId from the CURRENT workflow (not stale)
    // 2. We have a workflowUrl (for caching)
    // 3. The workflow URL matches the current workflow (prevents stale data)
    // 4. There are no media items
    // 5. We haven't already loaded one FOR THIS WORKFLOW in this component instance
    useEffect(() => {
        // Use rapidDataId from currentWorkflow to ensure we're using the correct one
        // currentRapidData can be stale when switching workflows
        const rapidDataId = currentWorkflow?.rapidDataId;
        const workflowMatches = currentWorkflow?.url === workflowUrl;
        const hasMedia = mediaItems.length > 0;
        const hasRapidData = !!rapidDataId;
        const hasWorkflowUrl = !!workflowUrl;
        const hasAlreadyLoadedForThisWorkflow =
            hasLoadedForCurrentWorkflowRef.current === workflowUrl;
        const isCurrentlyLoading = workflowUrl ? loadingWorkflowsSet.has(workflowUrl) : false;

        console.log("[useRandomSourceLoader] ========== Effect check ==========");
        console.log("[useRandomSourceLoader] workflowUrl:", workflowUrl);
        console.log("[useRandomSourceLoader] currentWorkflow.url:", currentWorkflow?.url);
        console.log("[useRandomSourceLoader] workflowMatches:", workflowMatches);
        console.log("[useRandomSourceLoader] rapidDataId (from workflow):", rapidDataId);
        console.log(
            "[useRandomSourceLoader] hasMedia:",
            hasMedia,
            "mediaItems.length:",
            mediaItems.length
        );
        console.log("[useRandomSourceLoader] hasRapidData:", hasRapidData);
        console.log("[useRandomSourceLoader] hasWorkflowUrl:", hasWorkflowUrl);
        console.log(
            "[useRandomSourceLoader] hasAlreadyLoadedForThisWorkflow:",
            hasAlreadyLoadedForThisWorkflow
        );
        console.log("[useRandomSourceLoader] isCurrentlyLoading:", isCurrentlyLoading);
        console.log(
            "[useRandomSourceLoader] hasLoadedForCurrentWorkflowRef:",
            hasLoadedForCurrentWorkflowRef.current
        );
        console.log("[useRandomSourceLoader] loadingWorkflows:", Array.from(loadingWorkflowsSet));

        if (
            workflowMatches &&
            hasRapidData &&
            hasWorkflowUrl &&
            !hasMedia &&
            !hasAlreadyLoadedForThisWorkflow &&
            !isCurrentlyLoading
        ) {
            console.log(
                "[useRandomSourceLoader] Triggering load - workflow matches, no media and has rapidData, workflowUrl:",
                workflowUrl,
                "rapidDataId:",
                rapidDataId
            );
            // Pass values directly to avoid stale closure issues
            loadRandomSource(rapidDataId, workflowUrl);
        }
    }, [
        currentWorkflow?.url,
        currentWorkflow?.rapidDataId,
        workflowUrl,
        mediaItems.length,
        loadRandomSource
    ]);

    // Provide a no-args wrapper for external callers that uses current state
    const loadRandomSourceWithCurrentState = useCallback(() => {
        const rapidDataId = currentWorkflow?.rapidDataId;
        if (rapidDataId && workflowUrl && currentWorkflow?.url === workflowUrl) {
            loadRandomSource(rapidDataId, workflowUrl);
        }
    }, [currentWorkflow?.rapidDataId, currentWorkflow?.url, workflowUrl, loadRandomSource]);

    return { loadRandomSource: loadRandomSourceWithCurrentState, isLoadingRandomSource };
}
